import { useEffect, useState } from 'react'
import type { AxiosError } from 'axios'
import {
  addAuthorizedUser,
  getAuthorizedUsers,
  reinviteAuthorizedUser,
  removeAuthorizedUser,
  updateAuthorizedUser,
  type AuthorizedUser,
} from '@/services/authorizedUsers'
import { formatApiError } from '@/utils/apiError'
import { useAuth } from '@/contexts/AuthContext'
import SettingsSection from '@/components/SettingsSection'

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function friendlyAuthorizedUserError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const status = (error as AxiosError).response?.status
    if (status === 409) {
      return 'That email has already been invited.'
    }
    if (status === 403) {
      return 'The super admin account is protected and cannot be changed.'
    }
  }
  return formatApiError(error, fallback)
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

function UserGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.597.458 3 3 0 00-3-3.75 3 3 0 00-3 3.75c.896.081 1.77.22 2.597.458M15 19.128V18a3 3 0 00-3-3h-1.5m4.5 4.128V18a3 3 0 00-3-3h-1.5m4.5 4.128V18a6 6 0 00-6-6h-1.5M9 19.128V18a3 3 0 013-3h1.5m-4.5 4.128V18a6 6 0 016-6h1.5M12 11.25a3 3 0 100-6 3 3 0 000 6z"
      />
    </svg>
  )
}

export default function AuthorizedUsersSection() {
  const { user } = useAuth()
  const [users, setUsers] = useState<AuthorizedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [reinvitingEmail, setReinvitingEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAuthorizedUsers()
      setUsers(response.users)
    } catch (err) {
      setError(friendlyAuthorizedUserError(err, 'Failed to load authorized users'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadUsers()
  }, [])

  const handleAdd = async () => {
    const email = newEmail.trim()
    if (!email) return

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await addAuthorizedUser(email)
      setNewEmail('')
      setSuccess(`Invited ${email}`)
      await loadUsers()
    } catch (err) {
      setError(friendlyAuthorizedUserError(err, 'Failed to add user'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (authorizedUser: AuthorizedUser) => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await updateAuthorizedUser(authorizedUser.email, {
        is_active: !authorizedUser.is_active,
      })
      await loadUsers()
    } catch (err) {
      setError(friendlyAuthorizedUserError(err, 'Failed to update user'))
    } finally {
      setSaving(false)
    }
  }

  const handleReinvite = async (authorizedUser: AuthorizedUser) => {
    setReinvitingEmail(authorizedUser.email)
    setError(null)
    setSuccess(null)
    try {
      const result = await reinviteAuthorizedUser(authorizedUser.email)
      setSuccess(`Invite email sent to ${result.email}`)
    } catch (err) {
      setError(friendlyAuthorizedUserError(err, 'Failed to re-send invite email'))
    } finally {
      setReinvitingEmail(null)
    }
  }

  const handleRemove = async (authorizedUser: AuthorizedUser) => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await removeAuthorizedUser(authorizedUser.email)
      await loadUsers()
    } catch (err) {
      setError(friendlyAuthorizedUserError(err, 'Failed to remove user'))
    } finally {
      setSaving(false)
    }
  }

  const sectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-info-600)]/10 ring-1 ring-[var(--color-info-600)]/20">
      <UserGroupIcon className="h-5 w-5 text-[var(--color-info-700)]" />
    </div>
  )

  const inputClassName =
    'h-10 w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[var(--color-info-600)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info-500)]/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500'

  const buttonSecondaryClassName =
    'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'

  const buttonPrimaryClassName = 'btn btn-primary'

  return (
    <SettingsSection
      title="Authorized users"
      description="Inquisitor uses Google sign-in. Add a Gmail address here to let that person log in; anyone not on this list will be denied at the login screen."
      icon={sectionIcon}
    >
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {success}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:max-w-2xl">
        <input
          type="email"
          className={inputClassName}
          placeholder="user@gmail.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleAdd()
          }}
          disabled={saving}
        />
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={saving || !newEmail.trim()}
          className={buttonPrimaryClassName}
        >
          Add user
        </button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <SpinnerIcon className="h-4 w-4 animate-spin" />
            Loading allowlist…
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500">No authorized users yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="hidden px-4 py-3 text-left font-medium text-gray-600 sm:table-cell">
                    Added
                  </th>
                  <th className="hidden px-4 py-3 text-left font-medium text-gray-600 md:table-cell">
                    Last invited
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {users.map((authorizedUser) => {
                  const isSelf = authorizedUser.email === user?.email
                  const isSuperAdminRow = isSelf && user?.is_super_admin
                  const canMutate = !isSelf && !isSuperAdminRow

                  return (
                    <tr key={authorizedUser.email}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{authorizedUser.email}</p>
                        {isSelf ? <p className="text-xs text-gray-500">Signed in as you</p> : null}
                        {isSuperAdminRow ? (
                          <p className="text-xs text-gray-500">Super admin (protected)</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
                            authorizedUser.is_active
                              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
                              : 'bg-gray-50 text-gray-600 ring-1 ring-gray-100'
                          }`}
                        >
                          {authorizedUser.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 text-gray-600 sm:table-cell">
                        {formatDate(authorizedUser.created_at)}
                      </td>
                      <td className="hidden px-4 py-3 text-gray-600 md:table-cell">
                        {formatDate(authorizedUser.last_invited_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => void handleToggleActive(authorizedUser)}
                            disabled={saving || !canMutate}
                            className={buttonSecondaryClassName}
                            title={
                              isSuperAdminRow
                                ? 'Super admin cannot be deactivated'
                                : isSelf
                                  ? 'You cannot deactivate your own account'
                                  : authorizedUser.is_active
                                    ? 'Deactivate user'
                                    : 'Reactivate user'
                            }
                          >
                            {authorizedUser.is_active ? 'Deactivate' : 'Reactivate'}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleReinvite(authorizedUser)}
                            disabled={saving || isSelf || reinvitingEmail === authorizedUser.email}
                            className={buttonSecondaryClassName}
                            title={isSelf ? 'You cannot re-invite your own account' : 'Re-send invite email'}
                          >
                            {reinvitingEmail === authorizedUser.email ? (
                              <SpinnerIcon className="h-4 w-4 animate-spin" />
                            ) : null}
                            Re-invite
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleRemove(authorizedUser)}
                            disabled={saving || !canMutate}
                            className={`${buttonSecondaryClassName} text-red-600 hover:bg-red-50`}
                            title={
                              isSuperAdminRow
                                ? 'Super admin cannot be removed'
                                : isSelf
                                  ? 'You cannot remove your own account'
                                  : 'Remove user'
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SettingsSection>
  )
}
